import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";

// Default sections
import { PVRQualifierTier } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import SingleTiersLoader from "@/Layouts/components/Skeletons/SingleTiersLoader";
import TierItemsLayout from "@/Layouts/components/Tiers/TierItemsLayout";

// MUI
import { Grid, Stack } from "@mui/material";

// Constants
const MAX_INPUT_VALUE = 1000000;

export default function PVRQualifier(props) {
	const {
		sectionId = "pvrQualifier",
		qualifiers = [],
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;
	const { addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrorsMessages = getErrorsMessages();

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onQualifiersUpdate = (updatedQualifiers) => {
		isValid ? removeSectionError?.(sectionId) : addSectionError?.(sectionId);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(sectionId, null, updatedQualifiers);
		}
	};

	// States
	const [innerQualifiers, setInnerQualifiers] = useCallbackState([], onQualifiersUpdate);

	// Methods
	const handleAddTier = () => {
		const updated = [...innerQualifiers];
		const lastIndex = updated?.length ? updated.length - 1 : 0;
		const adjustmentInput = updated?.[lastIndex]?.["adjustment"]?.["input"] || "Percent";

		if (updated?.length) {
			updated[lastIndex].to.value = 0;
		}

		updated.push(PVRQualifierTier({ from: 0, to: MAX_INPUT_VALUE, adjustment: 0, adjustmentInput }));
		wasUpdated.current = false;
		setInnerQualifiers(updated);
	};

	const handleDeleteTier = () => {
		const updated = [...innerQualifiers];
		if (updated.length > 1) {
			updated.splice(-1);
		}
		const lastIndex = updated?.length ? updated.length - 1 : 0;
		updated[lastIndex].to.value = MAX_INPUT_VALUE;
		update(updated);
	};

	const handleUpdateInput = (area, input) => {
		const updated = [...innerQualifiers];
		updated?.forEach((tier) => {
			tier[area]["input"] = input;
			tier[area]["value"] = 0;
		});
		wasUpdated.current = false;
		setInnerQualifiers(updated);
	};

	const handleUpdateValue = (index, area, value) => {
		const updated = [...innerQualifiers];
		updated[index][area]["value"] = value ? Number(value) : 0;
		if (updated?.[index + 1] && area === "to") {
			updated[index + 1]["from"]["value"] = Number(value) + 0.01;
		}
		update(updated);
	};

	const update = (updatedQualifiers = []) => {
		wasUpdated.current = true;
		setInnerQualifiers(updatedQualifiers);
	};

	useMemo(() => {
		if (innerQualifiers.length) {
			validate([
				{
					definition: {
						useKey: "pvr",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Dollar: 0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerQualifiers || [],
				},
				{
					definition: {
						useKey: "adjustment",
						operators: {
							conditions: [
								{
									type: "STEP",
									on: "value",
									compare: "<",
									deltas: { Percent: 0, Dollar: 0 },
								},
							],
						},
						errorsMap: ["adjustment"],
						message: `Wrong adjustments definition`,
						validationType: "SIMPLE",
					},
					section: innerQualifiers || [],
				},
			]);
		}
	}, [innerQualifiers, validate]);

	useEffect(() => {
		setInnerQualifiers(clone(qualifiers));
		return () => {
			wasUpdated.current = false;
			setInnerQualifiers([]);
			clearSectionErrors?.(sectionId);
		};
	}, [sectionId, qualifiers, setInnerQualifiers, clearSectionErrors]);

	return (
		<Grid container mt={2} sx={{ padding: "0 32px" }}>
			{loading ? (
				<SingleTiersLoader buttons sx={{ padding: 0 }} />
			) : (
				<Grid container item xs={12}>
					<Stack>
						<TierItemsLayout
							minimumRows={2}
							columnSpacing={4}
							rows={innerQualifiers}
							columns={[
								{
									_id: "pvr",
									title: "Individual PVR",
									component: {
										type: "DoubleInput",
										order: ["from", "to"],
										disableCommonInputs: true,
										properties: {
											firstInput: {
												data: [
													{
														type: "Dollar",
														min: 0,
														max: MAX_INPUT_VALUE,
														decimalsEditable: true,
														decimals: 2,
														disabled: true,
													},
												],
												sx: { maxWidth: "100px" },
											},
											secondInput: {
												data: [
													{
														type: "Dollar",
														min: 0,
														max: MAX_INPUT_VALUE,
														decimalsEditable: true,
														decimals: 2,
													},
												],
												sx: { maxWidth: "100px" },
											},
										},
										callbacks: {
											onActionUpdate: (positions, area, value) => {
												handleUpdateValue(positions.row, area, value);
											},
										},
									},
								},
								{
									_id: "adjustment",
									title: "Adjustment",
									component: {
										type: "InputForm",
										order: ["adjustment"],
										properties: {
											input: {
												data: [
													{
														type: "Percent",
														min: -100,
														max: 100,
														decimalsEditable: true,
														decimals: 2,
													},
													{
														type: "Dollar",
														min: 0,
														max: MAX_INPUT_VALUE,
														decimalsEditable: true,
														decimals: 2,
													},
												],
												sx: { maxWidth: "95px" },
											},
										},
										callbacks: {
											onActionUpdate: (positions, area, value) => {
												handleUpdateValue(positions.row, area, value);
											},
											onChangeType: (_positions, area, _, input) => {
												handleUpdateInput(area, input);
											},
										},
									},
								},
							]}
							errors={errors}
							errorsMessages={localErrorsMessages}
							readOnly={readOnly}
							onActionAdd={() => handleAddTier()}
							onActionDelete={() => handleDeleteTier()}
						/>
					</Stack>
				</Grid>
			)}
		</Grid>
	);
}
PVRQualifier.propTypes = {
	sectionId: PropTypes.string,
	qualifiers: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
